/* ==========================================================================
   FAQ page behaviour — Primerry
   · Accordion open/close (animated, accessible)
   · Sticky category tab bar with smooth scroll + scroll-spy
   Works with the Shopify theme editor (re-initialises on section load).
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function categories() {
    return toArray(document.querySelectorAll('[data-faq-category]'));
  }

  // Horizon scrolls `.page-wrapper` instead of `window` at desktop widths
  // (>= 990px). Always resolve the active scrolling element so sticky tabs,
  // scroll-spy and anchor correction work on both desktop and mobile.
  function scrollContainer() {
    var wrapper = document.querySelector('.page-wrapper');

    if (wrapper) {
      var styles = window.getComputedStyle(wrapper);
      var canScroll = /(auto|scroll|overlay)/.test(styles.overflowY);
      if (canScroll) return wrapper;
    }

    return window;
  }

  function scrollByY(amount, behavior) {
    var scroller = scrollContainer();
    var options = { top: amount, behavior: behavior || 'auto' };

    if (scroller === window) {
      window.scrollBy(options);
    } else if (typeof scroller.scrollBy === 'function') {
      scroller.scrollBy(options);
    } else {
      scroller.scrollTop += amount;
    }
  }

  function isAtPageBottom() {
    var scroller = scrollContainer();

    if (scroller === window) {
      var doc = document.scrollingElement || document.documentElement;
      return window.innerHeight + doc.scrollTop >= doc.scrollHeight - 2;
    }

    return scroller.clientHeight + scroller.scrollTop >= scroller.scrollHeight - 2;
  }

  /* ------------------------------------------------------------ accordion */

  function setOpen(item, open) {
    var trigger = item.querySelector('[data-faq-trigger]');
    var panel = item.querySelector('[data-faq-panel]');

    item.classList.toggle('is-open', open);
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.setAttribute('aria-hidden', open ? 'false' : 'true');
  }

  function initAccordion(root) {
    toArray(root.querySelectorAll('[data-faq-trigger]')).forEach(function (trigger) {
      if (trigger.dataset.faqBound === 'true') return;
      trigger.dataset.faqBound = 'true';

      trigger.addEventListener('click', function () {
        var item = trigger.closest('[data-faq-item]');
        if (!item) return;

        var list = item.closest('[data-faq-list]');
        var willOpen = !item.classList.contains('is-open');

        // Optional "one question open at a time" behaviour.
        if (willOpen && list && list.dataset.faqSingle === 'true') {
          toArray(list.querySelectorAll('[data-faq-item].is-open')).forEach(function (other) {
            if (other !== item) setOpen(other, false);
          });
        }

        setOpen(item, willOpen);
      });
    });
  }

  /* ----------------------------------------------------------------- tabs */

  function initTabs(bar) {
    if (!bar || bar.dataset.faqBound === 'true') return;
    bar.dataset.faqBound = 'true';

    var scroller = bar.querySelector('[data-faq-tabs-scroller]');
    var tabs = toArray(bar.querySelectorAll('[data-faq-tab]'));
    if (!tabs.length) return;

    var lockUntil = 0; // pause scroll-spy right after a click

    // Candidates for a header that stays pinned while the page scrolls.
    var HEADER_SELECTOR = [
      '#header-group',
      '.shopify-section-group-header-group',
      '[data-sticky-header]',
      'sticky-header',
      '.section-header',
      '.header-wrapper'
    ].join(',');

    // How far down the viewport the pinned header reaches, in px.
    function pinnedHeaderHeight() {
      var deepest = 0;

      toArray(document.querySelectorAll(HEADER_SELECTOR)).forEach(function (el) {
        if (el === bar || el.contains(bar)) return;

        var styles = window.getComputedStyle(el);
        if (styles.position !== 'sticky' && styles.position !== 'fixed') return;
        if (styles.display === 'none' || styles.visibility === 'hidden') return;

        var height = el.getBoundingClientRect().height;
        if (!height) return;

        // Where this element's bottom edge sits once it is pinned.
        var top = parseFloat(styles.top);
        if (isNaN(top)) top = 0;

        deepest = Math.max(deepest, top + height);
      });

      return deepest;
    }

    function stickyTop() {
      var offset = parseInt(bar.dataset.offsetTop || '0', 10);
      if (isNaN(offset)) offset = 0;

      if (bar.dataset.autoHeader !== 'false') {
        offset += pinnedHeaderHeight();
      }

      // Horizon publishes its measured values on <body>. Prefer the complete
      // header-group height (announcement + navigation), then header height.
      if (bar.dataset.autoHeader !== 'false') {
        var bodyStyles = getComputedStyle(document.body);
        var rootStyles = getComputedStyle(document.documentElement);
        var headerGroupVar = parseFloat(
          bodyStyles.getPropertyValue('--header-group-height') ||
          rootStyles.getPropertyValue('--header-group-height')
        );
        var headerVar = parseFloat(
          bodyStyles.getPropertyValue('--header-height') ||
          rootStyles.getPropertyValue('--header-height')
        );
        var baseOffset = parseInt(bar.dataset.offsetTop || '0', 10);
        if (isNaN(baseOffset)) baseOffset = 0;

        if (!isNaN(headerGroupVar)) {
          offset = Math.max(offset, baseOffset + headerGroupVar);
        } else if (!isNaN(headerVar)) {
          offset = Math.max(offset, baseOffset + headerVar);
        }
      }

      return Math.round(offset);
    }

    var lastTop = null;

    var lastHeight = 0;

    function measure() {
      var top = stickyTop();

      // While pinned the spacer holds the bar's in-flow height.
      var height = bar.classList.contains('is-pinned')
        ? placeholder.offsetHeight || lastHeight
        : bar.getBoundingClientRect().height;
      if (height) lastHeight = height;

      if (top !== lastTop) {
        lastTop = top;
        document.documentElement.style.setProperty('--faq-tabbar-top', top + 'px');
      }
      document.documentElement.style.setProperty('--faq-scroll-offset', top + height + 'px');

      return top + height;
    }

    function targetFor(tab) {
      var key = tab.dataset.faqTarget;
      if (key) {
        var byId = document.getElementById(key);
        if (byId) return byId;
        var byKey = document.querySelector('[data-faq-key="' + key + '"]');
        if (byKey) return byKey;
      }
      // Fall back to matching by position in the page.
      return categories()[tabs.indexOf(tab)] || null;
    }

    function activate(tab) {
      tabs.forEach(function (other) {
        var on = other === tab;
        other.classList.toggle('is-active', on);
        if (on) {
          other.setAttribute('aria-current', 'true');
        } else {
          other.removeAttribute('aria-current');
        }
      });

      // Keep the active tab visible when the bar scrolls horizontally.
      if (scroller && scroller.scrollWidth > scroller.clientWidth) {
        var left = tab.offsetLeft - (scroller.clientWidth - tab.offsetWidth) / 2;
        scroller.scrollTo({ left: left, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }

    // The browser's own anchor navigation is used for the jump: it honours
    // scroll-margin-top and scroll-behavior, and works whatever element is doing
    // the scrolling. All we do here is refresh the offset and the active state.
    function goTo(tab) {
      var target = targetFor(tab);
      if (!target) return;

      measure();
      lockUntil = Date.now() + 1000;
      activate(tab);

      // Re-check once the smooth scroll has settled — the header can resize
      // mid-scroll (condensing, hide-on-scroll, lazily loaded logo).
      window.setTimeout(function () {
        var offset = measure();
        var drift = target.getBoundingClientRect().top - offset;
        if (Math.abs(drift) > 2) scrollByY(drift, 'auto');
        lockUntil = Date.now() + 120;
      }, reduceMotion ? 0 : 820);
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        // No preventDefault: the href jump is what moves the page.
        goTo(tab);
      });

      tab.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight') next = tabs[index + 1] || tabs[0];
        if (event.key === 'ArrowLeft') next = tabs[index - 1] || tabs[tabs.length - 1];
        if (event.key === 'Home') next = tabs[0];
        if (event.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        event.preventDefault();
        next.focus();
        next.click();
      });
    });

    /* ------------------------------------------------------- pinning */

    // A zero-height spacer sits where the bar lives in the flow. When the bar
    // goes fixed the spacer takes on its height, so the page never jumps.
    var placeholder = document.createElement('div');
    placeholder.className = 'faq-tabs__placeholder';
    if (bar.parentNode) bar.parentNode.insertBefore(placeholder, bar);

    var pinned = false;

    function syncPin() {
      var top = stickyTop();

      // Use viewport geometry rather than window.pageYOffset. Horizon's desktop
      // layout scrolls `.page-wrapper`, so window.pageYOffset stays at 0.
      var shouldPin = placeholder.getBoundingClientRect().top <= top;

      if (shouldPin === pinned) return;

      pinned = shouldPin;
      // Height is read before the class flips, while the bar is still in flow.
      var height = bar.getBoundingClientRect().height;
      bar.classList.toggle('is-pinned', shouldPin);
      placeholder.style.height = shouldPin ? height + 'px' : '0px';
    }

    /* -------------------------------------------------------- scroll-spy */

    var ticking = false;

    function spy() {
      ticking = false;

      // Always refresh the offset — the header may resize as the page scrolls.
      var offset = measure() + 4;
      syncPin();
      if (Date.now() < lockUntil) return;

      var sections = tabs.map(targetFor);
      var activeIndex = 0;

      sections.forEach(function (section, i) {
        if (section && section.getBoundingClientRect().top <= offset) activeIndex = i;
      });

      // At the very bottom of the page, always light up the last tab.
      var atBottom = isAtPageBottom();
      if (atBottom) activeIndex = tabs.length - 1;

      if (tabs[activeIndex] && !tabs[activeIndex].classList.contains('is-active')) {
        activate(tabs[activeIndex]);
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(spy);
    }

    // Mobile uses the document/window scroller. Horizon desktop (>= 990px)
    // uses `.page-wrapper`. Listen to both; requestAnimationFrame de-duplicates
    // the work and makes this resilient across responsive breakpoint changes.
    window.addEventListener('scroll', onScroll, { passive: true });
    var pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper) pageWrapper.addEventListener('scroll', onScroll, { passive: true });

    window.addEventListener('resize', function () {
      measure();
      syncPin();
      onScroll();
    });

    if (window.ResizeObserver) {
      var observer = new ResizeObserver(function () {
        measure();
        syncPin();
      });
      observer.observe(bar);
      toArray(document.querySelectorAll(HEADER_SELECTOR)).forEach(function (el) {
        if (el !== bar && !el.contains(bar)) observer.observe(el);
      });
    }

    window.addEventListener('load', function () {
      measure();
      onScroll();
    });

    measure();
    syncPin();

    // Honour a #hash landing on the category anchors.
    if (window.location.hash) {
      var landing = tabs.filter(function (tab) {
        var target = targetFor(tab);
        return target && '#' + target.id === window.location.hash;
      })[0];
      if (landing) {
        window.requestAnimationFrame(function () {
          measure();
          activate(landing);
          var target = targetFor(landing);
          if (target) target.scrollIntoView({ block: 'start', behavior: 'auto' });
        });
      }
    } else {
      spy();
    }
  }

  /* ------------------------------------------------------------------ run */

  function init(root) {
    var scope = root || document;
    initAccordion(scope);
    toArray(scope.querySelectorAll('[data-faq-tabs]')).forEach(initTabs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: sections are swapped in and out of the DOM.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    var item = event.target.querySelector('[data-faq-item]');
    if (item) setOpen(item, true);
  });
})();